import { decimalRounding } from 'yoda-site-components/lib/helpers/Pricing/Pricing';
import {
    initProductOptions,
    getSelectedColor,
    getSelectedSize,
} from 'yoda-site-components/lib/selectors/ProductOptionsSelector';
import productPricingSelector, {
    productPricingSelectorForAllLot,
} from 'yoda-site-components/lib/selectors/ProductPricingSelector';
import { selectMarketPlaceSellerInfo } from 'yoda-site-components/lib/selectors/MarketPlaceSelector';
import {
    selectProductDetailsWrapper,
    selectInventoryWrapper,
    selectSelectedQuantityWrapper,
} from 'yoda-site-components/lib/selectors/PdpNameSpaceSelector';
import productConstants from 'yoda-site-components/lib/common/ProductConstants';

export const getStoreAvailability = (channelOffering) => {
    if (channelOffering === 'Omnichannel') return 'both';
    if (channelOffering === 'OnlineOnly') return 'online only';
    return '';
};

export const parsePriceString = (priceStr) => {
    if (!priceStr || !priceStr.length) return '0.00';
    const raw = priceStr.split('- ')[priceStr.split('- ').length - 1];
    return decimalRounding(raw.replace(/[$,]/g, ''));
};

export const getPricingCadence = (type, marketingLabel, promotions) => {
    if (type && type.length !== 0) return type;
    if (marketingLabel || promotions) return 'buy more and save';
    return 'regular';
};

/**
 * Filters mapped product to selected lot only for lotId and lotDetails.
 */
export const filterToSelectedLot = (mapped) => {
    const { matchedLotId } = mapped;
    return {
        lotId: matchedLotId ? [matchedLotId] : mapped.lotId,
        lotDetails: matchedLotId
            ? (mapped.lotDetails || []).filter((ld) => ld.id === matchedLotId)
            : mapped.lotDetails,
    };
};

export const buildLotDetailsFromPricing = (pricingForAllLots, lots) => {
    if (!pricingForAllLots || !pricingForAllLots.length) {
        return (lots || []).map((lot) => ({
            id: lot.id || '',
            numberOfSKU: (lot.items || []).length,
        }));
    }
    return pricingForAllLots.map((lotPricing) => {
        const {
            defaults,
            original,
            fpac,
            type,
            marketingLabel,
            promotions,
            lotId,
            mapPrice,
            isCouponAmount,
        } = lotPricing;
        const matchedLot = lots.find((l) => l.id === lotId) || {};
        const items = matchedLot.items || [];
        const oosCount = items.filter((item) => !item.atp).length;
        const hasStock = items.some((item) => item.atp);
        return {
            id: lotId || '',
            numberOfSKU: items.length,
            currentPrice: !mapPrice ? parsePriceString(defaults) : '',
            listPrice: !mapPrice ? parsePriceString(original) || parsePriceString(defaults) : '',
            priceWithCoupon:
                !mapPrice && hasStock && isCouponAmount && fpac && fpac.length
                    ? decimalRounding(String(fpac[0].amount.min).replace('$', ''))
                    : '',
            pricingCadence: getPricingCadence(type, marketingLabel, promotions),
            skuPercentageOOS: items.length ? Math.round((oosCount / items.length) * 100) : 0,
        };
    });
};

export const getCurrentPageUrl = () =>
    typeof window !== 'undefined' && window.location ? window.location.pathname : '';

/**
 * Reads productDetails & inventory from state and maps to CJA product schema.
 */
export const mapProductFromState = (state, sourceMiniPDP = false) => {
    const productDetails = sourceMiniPDP
        ? selectProductDetailsWrapper(sourceMiniPDP)(state) || {}
        : state.productDetails || {};
    const inventory = sourceMiniPDP
        ? selectInventoryWrapper(sourceMiniPDP)(state) || []
        : state.inventory || [];
    const hostname = (state.context || {}).hostname || 'www.jcpenney.com';
    const lots = productDetails.lots || [];
    const firstLot = lots[0] || {};
    const hasStock =
        inventory.length && inventory[0] && inventory[0].inventory
            ? inventory[0].inventory.some((item) => item.atp)
            : false;

    const baseProduct = {
        lotId: lots.map((lot) => lot.id),
        lotDetails: lots.map((lot) => ({
            id: lot.id || '',
            numberOfSKU: (lot.items || []).length,
        })),
        id: productDetails.id || '',
        name: (productDetails.name || '').toLowerCase(),
        link: productDetails.pdpURL ? `https://${hostname}${productDetails.pdpURL}` : '',
        inventoryStatus: hasStock ? 'in stock' : 'out of stock',
        storeAvailability: getStoreAvailability(firstLot.channelOffering),
        averageRating: productDetails.valuation?.rating ? `${productDetails.valuation.rating}` : '',
        review: productDetails.valuation?.reviews?.count
            ? `${productDetails.valuation.reviews.count}`
            : '',
        imgSource: (productDetails.images || [])[0]?.url || '',
        categoryId: productDetails.category?.id || '',
        departmentId: productDetails.category?.parent?.id || '',
        flags: {
            isCollection: !!(productDetails.collection && productDetails.collection.length),
            isCarePlan: !!firstLot.warranties,
            isBundle: productDetails.templateType === 'bundle',
        },
        type: productDetails.templateType || '',
        categoryName: productDetails.category?.name || '',
        brand: productDetails.brand?.name || '',
    };

    try {
        const pricing = productPricingSelector(sourceMiniPDP)(state) || {};
        const pricingForAllLots = productPricingSelectorForAllLot(sourceMiniPDP)(state) || [];
        const productOptions = initProductOptions(sourceMiniPDP)(state) || {};
        const marketPlaceInfo = selectMarketPlaceSellerInfo(sourceMiniPDP)(state) || {};
        const selectedColor = getSelectedColor(sourceMiniPDP)(state);
        const selectedSize = getSelectedSize(sourceMiniPDP)(state);

        const {
            defaults,
            original,
            type,
            surcharges,
            marketingLabel,
            mapPrice,
            promotions,
            fpac,
            isCouponAmount,
        } = pricing;
        const canShowPrice = !mapPrice;
        const sellingPrice = canShowPrice ? parsePriceString(defaults) : '';
        const listPrice = canShowPrice ? parsePriceString(original) || sellingPrice : '';
        const priceWithCoupon =
            canShowPrice && hasStock && isCouponAmount && fpac && fpac.length
                ? decimalRounding(String(fpac[0].amount.min).replace('$', ''))
                : '';

        const filteredSKU = (productOptions.filteredItems || [])[0] || {};
        const featureAttributes = (firstLot.productIcons || [])
            .flatMap((item) => (item.valueIcons || []).map((v) => v.value))
            .filter(Boolean);
        const hasEcoRebate = lots.some((lot) =>
            (lot.items || []).some((item) => item.rebatesAvailable)
        );
        const mpSellerName = marketPlaceInfo?.sellerName || productConstants.JCP_SELLER_NAME;

        return {
            ...baseProduct,
            lotDetails: buildLotDetailsFromPricing(pricingForAllLots, lots),
            listPrice,
            sellingPrice,
            surchargePrice: surcharges && surcharges.length ? surcharges : '0.00',
            pricingCadence: getPricingCadence(type, marketingLabel, promotions),
            priceWithCoupon,
            SKU: filteredSKU.id || '',
            size: selectedSize || '',
            color: selectedColor || '',
            marketingLabel: marketingLabel ? marketingLabel.toLowerCase() : '',
            itemType: (firstLot.itemType || '').toLowerCase(),
            vendorName: mpSellerName,
            badge: (productDetails.badges || [])[0] || '',
            feature: featureAttributes,
            sellerName: mpSellerName,
            sellerType: marketPlaceInfo?.isMarketPlaceProduct
                ? productConstants.MARKETPLACE_SELLER_TYPE
                : productConstants.JCP_SELLER_TYPE,
            flags: {
                ...baseProduct.flags,
                isGiftRegistry: !!(
                    productDetails.restrictions && productDetails.restrictions.canAddToRegistry
                ),
                isEcoRebate: hasEcoRebate,
                isCouponPricing: !!isCouponAmount,
                isUrgencyMessageShown: !!(
                    state.urgencyStatements &&
                    (state.urgencyStatements.viewMessage ||
                        state.urgencyStatements.addToCartMessage ||
                        state.urgencyStatements.soldMessage)
                ),
            },
            matchedLotId: filteredSKU.id
                ? (
                      lots.find((lot) =>
                          (lot.items || []).some((item) => item.id === filteredSKU.id)
                      ) || {}
                  ).id || ''
                : '',
            selectedQuantity: selectSelectedQuantityWrapper(sourceMiniPDP)(state),
        };
    } catch (e) {
        return baseProduct;
    }
};
